import { Avatar, Button, Dialog, Menu, MenuItem, Popover } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as AddUserMenuIcon } from "assets/add-user-menu.svg";
import { ReactComponent as DeleteIcon } from "assets/delete-icon.svg";
import { ReactComponent as RenameIcon } from "assets/pencil-icon.svg";
import { ReactComponent as ProjectIcon } from "assets/project-icon.svg";
import { ReactComponent as StarIcon } from "assets/star.svg";
import { ReactComponent as XMark } from "assets/x-mark.svg";
import { ReactComponent as YellowStar } from "assets/yellow-star.svg";
import { SLAPSHOT_INVITE_USER, SLAPSHOT_PROJECT_URL } from "constants/urls";
import { ProjectDetail } from "models/Project";
import { Workspace } from "models/Workspace";
import {
  deleteProject,
  renameProject,
  starProject,
  unstarProject,
} from "services/ProjectService";
import {
  getRelativeDate,
  getTextInitials,
  getUserNameText,
} from "utils/helper";

import styles from "./Project.module.scss";

interface ProjectItemProps {
  workspace: Workspace;
  project: ProjectDetail;
  starredProjects: ProjectDetail[];
  setStarredProjects: React.Dispatch<React.SetStateAction<ProjectDetail[]>>;
  updateProject: (p: ProjectDetail | null) => void;
}

const ProjectItem: React.FC<ProjectItemProps> = ({
  workspace,
  project,
  starredProjects,
  setStarredProjects,
  updateProject,
}) => {
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState<{
    mouseX: number | null;
    mouseY: number | null;
  }>({ mouseX: null, mouseY: null });
  const [thumbnailIndex, setThumbnailIndex] = useState(0);
  const [showRenameProject, setShowRenameProject] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const renameProjectEl = useRef<HTMLInputElement | null>(null);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (showRenameProject) {
      renameProjectEl.current?.focus();
    }
  }, [showRenameProject]);

  const handleMouseMove = (id, e, thumbnails) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - left;
    const percentage = mouseX / width;
    const newIndex = Math.floor(percentage * thumbnails.length);

    setThumbnailIndex(newIndex);
    setHovered(true);
  };

  const handleMouseLeave = (id) => {
    setThumbnailIndex(0);
    setHovered(false);
  };

  const handleStarProject = async (projectData: ProjectDetail) => {
    if (starredProjects.some((p) => p.id === projectData.id)) {
      unstarProject(projectData.id).then((res) => {
        setStarredProjects((prev) =>
          prev.filter((p) => p.id !== projectData.id),
        );
      });
    } else {
      starProject(projectData.id).then((res) => {
        setStarredProjects((prev) => [
          { ...projectData, is_starred: true },
          ...prev,
        ]);
      });
    }
    updateProject({ ...projectData, is_starred: !projectData.is_starred });
  };

  const handleClose = () => {
    setMousePosition({ mouseX: null, mouseY: null });
  };

  return (
    <div
      className={styles.projectContainer}
      key={project.id}
      onClick={() => {
        navigate(
          `${SLAPSHOT_PROJECT_URL.replace(
            ":projectId",
            project.id.toString(),
          )}`,
        );
      }}
      onContextMenu={(e) => {
        if (workspace && !workspace.is_admin) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        setMousePosition({
          mouseX: e.clientX - 2,
          mouseY: e.clientY - 4,
        });
      }}
      onMouseMove={(e) =>
        handleMouseMove(project.id, e, project.shot_thumbnails)
      }
      onMouseLeave={() => handleMouseLeave(project.id)}
      onMouseEnter={() => setHovered(true)}
    >
      {(hovered || project.is_starred) && (
        <button
          type="button"
          className={styles.starProject}
          onClick={(e) => {
            e.stopPropagation();
            handleStarProject(project);
          }}
        >
          {project.is_starred ? <YellowStar /> : <StarIcon />}
        </button>
      )}
      {project.shot_thumbnails.length > 0 ? (
        <div className={styles.projectThumbnail}>
          <img
            className={styles.thumbnailImage}
            src={project.shot_thumbnails[thumbnailIndex]}
            alt={project.shot_thumbnails[thumbnailIndex]}
          />
        </div>
      ) : (
        <div className={styles.iconContainer}>
          <ProjectIcon />
        </div>
      )}
      <div className={styles.projectDetail}>
        <div className={styles.projectName}>
          {showRenameProject && project ? (
            <input
              type="text"
              className={styles.renameProject}
              defaultValue={project.name}
              ref={renameProjectEl}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              onBlur={(e) => {
                if (e.relatedTarget?.classList.contains("MuiMenu-paper"))
                  return;
                const newName = e.currentTarget.value.trim();
                if (newName !== project.name && newName.length) {
                  renameProject(project.id, newName).then((res) => {
                    updateProject({ ...project, name: res.name });
                    setStarredProjects((prev) =>
                      prev.map((p) =>
                        p.id !== project.id ? p : { ...p, name: newName },
                      ),
                    );
                  });
                }
                setShowRenameProject(false);
              }}
            />
          ) : (
            <h3>{project.name}</h3>
          )}
          <p>{getRelativeDate(project.created)}</p>
        </div>
        <div className={styles.projectInfo}>
          <h5>
            {project.shots_count === 1
              ? `${project.shots_count} Asset`
              : `${project.shots_count} Assets`}
          </h5>
          <div
            className={styles.projectUsers}
            // onMouseEnter={(e) => {
            //   setMembersAnchor(null);
            //   setMembersAnchor(e.currentTarget);
            //   setMembers(project.users);
            // }}
          >
            {project.users.slice(0, 3).map((u, index) =>
              u.profile_picture_url ? (
                <img
                  key={u.id}
                  className={styles.projectUser}
                  src={u.profile_picture_url}
                  alt={u.profile_picture_url}
                  style={{
                    marginRight: "-5px",
                    zIndex: index,
                    border: "1px solid #27273A",
                  }}
                />
              ) : (
                <Avatar
                  key={u.id}
                  className={styles.projectUserAvatar}
                  {...{ children: getTextInitials(u.name) }}
                  sx={{
                    bgcolor: "purple",
                    width: 16,
                    height: 16,
                  }}
                  style={{
                    marginRight: "-5px",
                    zIndex: index,
                    border: "1px solid #27273A",
                  }}
                />
              ),
            )}
            {project.users.length > 3 && (
              <div
                className={styles.projectUserCount}
              >{`+${project.users.length - 3}`}</div>
            )}
            <span>
              {project.users.map((m, index) => (
                <React.Fragment key={index}>
                  {getUserNameText(m)}
                  <br />
                </React.Fragment>
              ))}
            </span>
          </div>
        </div>
      </div>
      <Dialog
        open={showConfirmationModal}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        <div className="modalContainer">
          <div className="modalHeader">
            <h3 className="modalTitle">Delete Project</h3>
            <button
              onClick={() => {
                setShowConfirmationModal(false);
              }}
              type="button"
              className="closeButton"
            >
              <XMark />
            </button>
          </div>
          <div className={styles.confirmationText}>
            <p>
              Are you sure you want to delete this project&nbsp;
              <b>{project.name}</b>? We highly recommend exporting your data
              before deleting
            </p>
          </div>
          <div className="modalActions">
            <Button
              type="button"
              className={styles.noBackgroundButton}
              onClick={(e) => {
                e.stopPropagation();
                setShowConfirmationModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={styles.outlineButtonRed}
              onClick={(e) => {
                e.stopPropagation();
                deleteProject(project.id).then(() => {
                  setShowConfirmationModal(false);
                  handleClose();
                  updateProject(null);
                  setStarredProjects((prev) =>
                    prev.filter((p) => p.id !== project.id),
                  );
                });
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Dialog>
      <Menu
        id="basic-menu"
        open={mousePosition.mouseY !== null}
        onClose={(e) => {
          if (
            e &&
            "stopPropagation" in e &&
            typeof e.stopPropagation === "function"
          ) {
            e.stopPropagation();
          }
          handleClose();
        }}
        anchorReference="anchorPosition"
        anchorPosition={
          mousePosition.mouseY !== null && mousePosition.mouseX !== null
            ? { top: mousePosition.mouseY, left: mousePosition.mouseX }
            : undefined
        }
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "#111113",
            borderRadius: "7px",
            border: "1px solid #CCCCDC1F",
            padding: "8px 8px",
            width: "184px",
            boxShadow: "0px 8px 24px 0px #010409",
          },
        }}
      >
        <MenuItem
          className={styles.menuItem}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `${SLAPSHOT_INVITE_USER}?workspaceId=${workspace?.id}&projectIds=${project.id}`,
            );
          }}
        >
          <AddUserMenuIcon style={{ marginRight: "8px" }} /> Invite member
        </MenuItem>
        {!project.is_demo_project && (
          <MenuItem
            className={styles.menuItem}
            onClick={(e) => {
              e.stopPropagation();
              setMousePosition({ mouseX: null, mouseY: null });
              setShowRenameProject(true);
            }}
          >
            <RenameIcon style={{ marginRight: "8px" }} />
            Rename
          </MenuItem>
        )}
        <MenuItem
          className={styles.menuItem}
          onClick={(e) => {
            e.stopPropagation();
            setMousePosition({ mouseX: null, mouseY: null });
            setShowConfirmationModal(true);
          }}
        >
          <DeleteIcon style={{ marginRight: "8px" }} />
          Delete
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ProjectItem;
